import React, { useState, useEffect } from "react";
import { usePermission } from "../../hooks/usePermission";
import EmissionKPI from "./EmissionKPI";
import UnitKpiSettings from "./Unit";
import GHGDbKPISettings from "./GHGDbSettingByKpi";
import EnergySourceSettings from "./EnergySources";
import KpiRequiredEvidences from "./KpiRequiredEvidences";
import KPIAssetSettings from "./KpiAssets";
import KpiFrequencySettings from "./KpiFrequency";
import DueDates from "./DueDates";

// ─── Sub-tab config ───────────────────────────────────────────────────────────

const KPI_SUBTABS = [
  { key: "active-kpis",         label: "Active KPIs",        icon: "chart-bar" },
  { key: "unit",                label: "Unit",               icon: "ruler" },
  { key: "ghg-databases",       label: "GHG Databases",      icon: "database" },
  { key: "energy-sources",      label: "Energy Sources",     icon: "bolt" },
  { key: "kpi-assets",          label: "KPI Assets",         icon: "industry" },
  { key: "required-evidences",  label: "Required Evidences", icon: "file-alt" },
  { key: "frequency",           label: "Frequency",          icon: "clock-rotate-left" },
  { key: "due-dates",           label: "Set Due Date",       icon: "calendar-check" },
];

// ─── Underline sub-nav ────────────────────────────────────────────────────────

function KpiSubNav({ activeKey, onChange, tabs }) {
  return (
    <div style={{
      display: "flex",
      gap: 0,
      borderBottom: "1.5px solid #e2e8f0",
      marginBottom: 20,
      overflowX: "auto",
      scrollbarWidth: "none",
    }}>
      {tabs.map(tab => {
        const isActive = activeKey === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "9px 18px",
              border: "none",
              borderBottom: isActive ? "2.5px solid #3f88a5" : "2.5px solid transparent",
              background: "transparent",
              color: isActive ? "#3f88a5" : "#6b7280",
              fontSize: 13,
              fontWeight: isActive ? 700 : 500,
              fontFamily: "inherit",
              cursor: "pointer",
              marginBottom: -1.5,
              transition: "color 0.15s, border-color 0.15s",
              outline: "none",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
            onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = "#1e293b"; }}
            onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = "#6b7280"; }}
          >
            <i className={`fas fa-${tab.icon}`} style={{ fontSize: 12 }} aria-hidden="true" />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function KpisGroup({ permissions, companyId }) {
  const [canViewActiveKpis] = usePermission("active-kpis.view", "KPIS");
  const [canViewUnit] = usePermission("unit.view", "UNIT");
  const [canViewGhg] = usePermission("ghg-databases.view", "GHG_SETUP");
  const [canViewEnergySources] = usePermission("energy-sources.view", "ENERGY_SOURCES");
  const [canViewKpiAssets] = usePermission("kpi-assets.view", "KPI_ASSETS");
  const [canViewRequiredEvidences] = usePermission("kpi-required-evidences.view", "REQUIRED_EVIDENCES");
  const [canViewFrequency] = usePermission("kpi-frequency.view", "KPIS");
  const [canViewDueDates] = usePermission("due-dates.view", "DUE_DATES");

  // Determine allowed sub-tabs dynamically
  const allowedSubTabs = KPI_SUBTABS.filter(tab => {
    if (tab.key === "active-kpis") return canViewActiveKpis;
    if (tab.key === "unit") return canViewUnit;
    if (tab.key === "ghg-databases") return canViewGhg;
    if (tab.key === "energy-sources") return canViewEnergySources;
    if (tab.key === "kpi-assets") return canViewKpiAssets;
    if (tab.key === "required-evidences") return canViewRequiredEvidences;
    if (tab.key === "frequency") return canViewFrequency;
    if (tab.key === "due-dates") return canViewDueDates;
    return true;
  });

  const [activeSubTab, setActiveSubTab] = useState(() => {
    return allowedSubTabs[0]?.key || "";
  });

  // Keep active tab in sync if allowed tabs change
  useEffect(() => {
    if (allowedSubTabs.length > 0) {
      if (!activeSubTab || !allowedSubTabs.some(t => t.key === activeSubTab)) {
        setActiveSubTab(allowedSubTabs[0].key);
      }
    }
  }, [canViewActiveKpis, canViewUnit, canViewGhg, canViewEnergySources, canViewKpiAssets, canViewRequiredEvidences, canViewFrequency, canViewDueDates, activeSubTab]);

  const renderContent = () => {
    // Double-check permissions before rendering
    if (activeSubTab === "active-kpis" && !canViewActiveKpis) return null;
    if (activeSubTab === "unit" && !canViewUnit) return null;
    if (activeSubTab === "ghg-databases" && !canViewGhg) return null;
    if (activeSubTab === "energy-sources" && !canViewEnergySources) return null;
    if (activeSubTab === "kpi-assets" && !canViewKpiAssets) return null;
    if (activeSubTab === "required-evidences" && !canViewRequiredEvidences) return null;
    if (activeSubTab === "frequency" && !canViewFrequency) return null;
    if (activeSubTab === "due-dates" && !canViewDueDates) return null;

    switch (activeSubTab) {
      case "active-kpis":        return <EmissionKPI />;
      case "unit":               return <UnitKpiSettings />;
      case "ghg-databases":      return <GHGDbKPISettings />;
      case "energy-sources":     return <EnergySourceSettings />;
      case "required-evidences": return <KpiRequiredEvidences />;
      case "kpi-assets":         return <KPIAssetSettings />;
      case "frequency":          return <KpiFrequencySettings />;
      case "due-dates":          return <DueDates />;
      default:                   return null;
    }
  };

  if (allowedSubTabs.length === 0) {
    return (
      <div style={{ padding: "40px 20px", textAlign: "center", color: "#64748b", fontSize: 13 }}>
        You do not have permission to view any KPI settings.
      </div>
    );
  }

  return (
    <div>
      <KpiSubNav activeKey={activeSubTab} onChange={setActiveSubTab} tabs={allowedSubTabs} />
      {renderContent()}
    </div>
  );
}